import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./lpr-workflow-list.component.html?ngResource";
import { Component, EventEmitter, Input, Output, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TreeModule } from 'primeng/tree';
import _ from 'lodash';
import { OrganizationService } from '../../../shared/services/organization.service';
import { SiteService } from '../../../shared/services/site.service';
import { ResourceListService } from '../../../shared/services/resource-list.service';
import { LPRManagementService } from 'src/ng-app/features/lpr-management';
import { filter, forkJoin, Subject, takeUntil } from 'rxjs';
const DEFAULT_NODE_ICON_MAP = new Map([
    ['org', 'fad fa-building'],
    ['workflow', 'fad fa-cog'],
    ['site', 'fad fa-map-marked'],
    ['resource', 'fad fa-image'],
]);
let LPRWorkflowListComponent = class LPRWorkflowListComponent {
    nodeSelectionOutputs = new Map([
        ['org', new EventEmitter()],
        ['workflow', new EventEmitter()],
    ]);
    expandOrCollapseTrigger;
    orgNodeSelected = this.nodeSelectionOutputs.get('org');
    workflowNodeSelected = this.nodeSelectionOutputs.get('workflow');
    nodes;
    organizationsModel;
    nodeTypeIconMap;
    treeNodesMap = new Map([
        ['org', new Map()],
        ['workflow', new Map()],
        ['site', new Map()],
        ['resource', new Map()]
    ]);
    childNodesMap = new Map([
        ['org', new Map()],
        ['workflow', new Map()],
    ]);
    parentNodeTypeMap = new Map([
        ['workflow', { parentType: 'org', key: 'orgId' }],
    ]);
    pendingNewWorkflows = new Set();
    selectedWorkflow = null;
    #destroy$ = new Subject();
    OrganizationService = inject(OrganizationService);
    SiteService = inject(SiteService);
    ResourceListService = inject(ResourceListService);
    LprManagementService = inject(LPRManagementService);
    constructor() {
        this.nodeTypeIconMap = DEFAULT_NODE_ICON_MAP;
    }
    ngOnInit() {
        forkJoin({
            orgs: this.OrganizationService.getOrganizations(),
            sites: this.SiteService.getSites(),
            resourceLists: this.ResourceListService.getResourceLists(),
            workflows: this.LprManagementService.getLprWorkflows(),
        })
            .pipe(takeUntil(this.#destroy$))
            .subscribe(({ orgs, sites, resourceLists, workflows }) => {
            this.buildTreeNodes(sites.array, 'site');
            this.buildTreeNodes(resourceLists.array, 'resource');
            this.buildTreeNodes(workflows, 'workflow');
            this.nodes = this.buildTreeNodes(orgs.array, 'org');
        });
        this.expandOrCollapseTrigger
            ?.pipe(takeUntil(this.#destroy$))
            .subscribe(expand => this.expandOrCollapseAll(expand, this.nodes));
        this.LprManagementService.workflowUpdated$
            .pipe(takeUntil(this.#destroy$))
            .subscribe(updatedWorkflow => this.updateWorkflowNode(updatedWorkflow));
        this.LprManagementService.selectedWorkflow$
            .pipe(takeUntil(this.#destroy$))
            .subscribe(workflow => this.selectedWorkflow = workflow);
        this.LprManagementService.selectedWorkflow$
            .pipe(takeUntil(this.#destroy$), filter(workflow => workflow?.isNew() && !this.pendingNewWorkflows.has(workflow)))
            .subscribe((newWorkflow) => {
            this.buildTreeNodes([newWorkflow], 'workflow');
            this.pendingNewWorkflows.add(newWorkflow);
            const { parentType, key } = this.parentNodeTypeMap.get('workflow');
            const parentKey = _.get(newWorkflow, key);
            if (this.treeNodesMap.has(parentType)) {
                const parentNodesMap = this.treeNodesMap.get(parentType);
                if (parentNodesMap.has(parentKey)) {
                    parentNodesMap.get(parentKey).expanded = true;
                }
            }
        });
    }
    ngOnDestroy() {
        this.#destroy$.next();
        this.#destroy$.complete();
    }
    buildTreeNodes(data, type) {
        const klass = this;
        const nodes = data.map((item) => {
            const node = {
                label: item.name,
                key: item.id,
                data: item,
                type,
                selectable: this.nodeSelectionOutputs.has(type),
                get children() {
                    if (klass.childNodesMap.has(type)) {
                        const childNodesMap = klass.childNodesMap.get(type);
                        if (childNodesMap.has(item.id)) {
                            return childNodesMap.get(item.id);
                        }
                    }
                    return [];
                }
            };
            if (this.nodeTypeIconMap.has(type)) {
                node.icon = this.nodeTypeIconMap.get(type);
            }
            if (this.treeNodesMap.has(type)) {
                this.treeNodesMap.get(type).set(item.id, node);
            }
            if (this.parentNodeTypeMap.has(type)) {
                this.getChildNodesForParent(item, type).push(node);
            }
            if (type === 'workflow') {
                this.setSiteAndListNodes(item);
            }
            return node;
        });
        return nodes;
    }
    setSiteAndListNodes(workflow) {
        const sites = _.reduce(workflow.sites, (acc, site) => {
            const siteId = _.get(site, 'id', site);
            const siteNode = this.treeNodesMap.get('site').get(siteId);
            if (siteNode) {
                acc.push(siteNode);
            }
            return acc;
        }, []);
        const resourceLists = _.reduce(workflow.resourcelists, (acc, list) => {
            const listId = _.get(list, 'id', list);
            const listNode = this.treeNodesMap.get('resource').get(listId);
            if (listNode) {
                acc.push(listNode);
            }
            return acc;
        }, []);
        this.childNodesMap.get('workflow').set(workflow.id, [
            {
                label: 'Sites',
                type: 'site',
                icon: this.nodeTypeIconMap.get('site'),
                selectable: false,
                children: sites
            },
            {
                label: 'Resource Lists',
                type: 'resource',
                icon: this.nodeTypeIconMap.get('resource'),
                selectable: false,
                children: resourceLists
            }
        ]);
    }
    updateWorkflowNode(workflow) {
        let workflowNode = this.treeNodesMap.get('workflow').get(workflow.id);
        if (!workflowNode) {
            for (const flow of this.pendingNewWorkflows.values()) {
                if (flow.id === workflow.id) {
                    this.pendingNewWorkflows.delete(flow);
                    const nodes = this.getChildNodesForParent(flow, 'workflow');
                    const found = _.find(nodes, node => node.data.id === workflow.id);
                    if (found) {
                        this.treeNodesMap.get('workflow').set(workflow.id, found);
                        workflowNode = found;
                    }
                    break;
                }
            }
        }
        workflowNode.label = workflow.name;
        this.setSiteAndListNodes(workflow);
    }
    getChildNodesForParent(item, type) {
        const { parentType, key } = this.parentNodeTypeMap.get(type);
        if (this.childNodesMap.has(parentType)) {
            const parentNodesMap = this.childNodesMap.get(parentType);
            const itemKey = _.get(item, key);
            if (!parentNodesMap.has(itemKey)) {
                parentNodesMap.set(itemKey, []);
            }
            return parentNodesMap.get(itemKey);
        }
        return [];
    }
    onNodeSelect(event) {
        const { node } = event;
        if (this.nodeSelectionOutputs.has(node.type)) {
            this.nodeSelectionOutputs.get(node.type).emit(node);
        }
    }
    expandOrCollapseAll(expand, nodes = []) {
        nodes.forEach((node) => {
            node.expanded = expand;
            if (node.children.length) {
                this.expandOrCollapseAll(expand, node.children);
            }
        });
    }
    static ctorParameters = () => [];
    static propDecorators = {
        expandOrCollapseTrigger: [{ type: Input }],
        orgNodeSelected: [{ type: Output }],
        workflowNodeSelected: [{ type: Output }]
    };
};
LPRWorkflowListComponent = __decorate([
    Component({
        selector: 'lpr-workflow-list',
        standalone: true,
        imports: [CommonModule, TreeModule],
        template: __NG_CLI_RESOURCE__0,
        encapsulation: ViewEncapsulation.None,
    })
], LPRWorkflowListComponent);
export { LPRWorkflowListComponent };
